import { useEffect, useState, type FormEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { getRow, saveRow } from '../../lib/db';
import { useToast } from '../../components/Toast';
import { QuillEditor } from '../../components/QuillEditor';

export function JobEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { notify } = useToast();
  const [loading, setLoading] = useState(Boolean(id));
  const [saving, setSaving] = useState(false);
  const [f, setF] = useState({
    title: '',
    status: 'published',
    content: '',
    workFromHome: false,
    salary: '' as string,
    currency: 'BGN',
  });

  useEffect(() => {
    if (!id) return;
    getRow('jobs', id).then((r) => {
      if (r)
        setF({
          title: String(r.title ?? ''),
          status: String(r.status ?? 'published'),
          content: String(r.content ?? ''),
          workFromHome: Boolean(r.workFromHome),
          salary: r.salary != null ? String(r.salary) : '',
          currency: String(r.currency ?? 'BGN'),
        });
      setLoading(false);
    });
  }, [id]);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (f.salary && !f.currency) {
      notify('danger', 'Currency is required when a salary is set.');
      return;
    }
    setSaving(true);
    try {
      const savedId = await saveRow('jobs', id, {
        title: f.title,
        status: f.status,
        content: f.content,
        workFromHome: f.workFromHome,
        salary: f.salary ? Number(f.salary) : null,
        currency: f.salary ? f.currency : null,
      });
      notify('success', 'Job saved.');
      navigate(`/jobs/${savedId}`);
    } catch (err) {
      notify('danger', (err as Error).message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <p className="text-muted">Loading…</p>;

  return (
    <form onSubmit={onSubmit} style={{ maxWidth: 720 }}>
      <h3 className="fw-bold mb-3">{id ? 'Edit job' : 'New job'}</h3>

      <div className="mb-3">
        <label className="form-label fw-semibold">Title *</label>
        <input
          className="form-control"
          required
          maxLength={255}
          value={f.title}
          onChange={(e) => setF({ ...f, title: e.target.value })}
        />
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Status *</label>
        <select
          className="form-select"
          value={f.status}
          onChange={(e) => setF({ ...f, status: e.target.value })}
        >
          <option value="published">published</option>
          <option value="stopped">stopped</option>
        </select>
      </div>

      <div className="mb-3">
        <label className="form-label fw-semibold">Description *</label>
        <QuillEditor value={f.content} onChange={(html) => setF({ ...f, content: html })} />
      </div>

      <div className="form-check mb-3">
        <input
          className="form-check-input"
          type="checkbox"
          id="wfh"
          checked={f.workFromHome}
          onChange={(e) => setF({ ...f, workFromHome: e.target.checked })}
        />
        <label className="form-check-label" htmlFor="wfh">
          Work from home
        </label>
      </div>

      <div className="row g-2 mb-3" style={{ maxWidth: 360 }}>
        <div className="col-7">
          <label className="form-label fw-semibold">Salary</label>
          <input
            type="number"
            min={0}
            className="form-control"
            value={f.salary}
            onChange={(e) => setF({ ...f, salary: e.target.value })}
          />
        </div>
        <div className="col-5">
          <label className="form-label fw-semibold">Currency</label>
          <select
            className="form-select"
            value={f.currency}
            onChange={(e) => setF({ ...f, currency: e.target.value })}
          >
            <option>BGN</option>
            <option>EUR</option>
            <option>USD</option>
          </select>
        </div>
      </div>

      <button className="btn btn-success" disabled={saving} type="submit">
        {saving ? 'Saving…' : 'Save'}
      </button>
    </form>
  );
}
